import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  GitBranch, Save, RotateCcw, FileDown, Loader2, AlertTriangle, History, RefreshCw, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { downloadExport } from "@/lib/downloadExport";
import { groupName } from "@/lib/leadGroups";

const ACTIONS = [
  { value: "new", label: "New" },
  { value: "unchanged", label: "Unchanged" },
];

const rowLabel = (r) => (r === "Unassigned" ? "Unassigned" : groupName(r));

/** Settings → Assigning Logic (PRD v2.0 Phase 3). The matrix is data: every
 *  cell decides whether a disposition save moves ownership to the saving agent. */
export default function AssigningLogicMatrix() {
  const { activeSlug } = useClient();
  const [cfg, setCfg] = useState(null);
  const [cells, setCells] = useState({});
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("save");        // "save" | "recalc"
  const [diag, setDiag] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-assigning-logic");
      setCfg(data);
      setCells(JSON.parse(JSON.stringify(data.cells || {})));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load assigning logic");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDiag = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-assigning-logic/diagnostics");
      setDiag(data);
    } catch { /* view permission covers this; ignore */ }
  }, []);

  useEffect(() => { load(); loadDiag(); }, [load, loadDiag, activeSlug]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const dirty = cfg && JSON.stringify(cells) !== JSON.stringify(cfg.cells);

  const setCell = (row, col, v) =>
    setCells((c) => ({ ...c, [row]: { ...(c[row] || {}), [col]: v } }));

  const pollJob = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/lead-assigning-logic/jobs/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(pollRef.current);
          load();
          loadDiag();
          if (data.status === "done") {
            toast.success(`Recalculation complete — ${data.summary?.leads_changed ?? 0} lead(s) reassigned`);
          } else {
            toast.error(`Recalculation stopped after ${data.processed ?? 0} lead(s): ${data.error || "unknown error"}. Ownership updates are idempotent — run it again to continue where it left off.`);
          }
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 1500);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/lead-assigning-logic/preview", { cells });
      setMode("save");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  /** Apply the CURRENT matrix to every lead without editing it — the fix for
   *  leads that were dispositioned before the matrix existed, or whose
   *  import-based owner was cleared by the call-log cleanup. */
  const runRecalcPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/lead-assigning-logic/recalculate?dry_run=true");
      setMode("recalc");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const downloadPreview = async () => {
    try {
      if (mode === "recover") {
        await downloadExport("/lead-assigning-logic/recover-no-history", { format: "csv" },
          `ownership-recovery-${activeSlug}.csv`, { method: "post", data: {} });
        return;
      }
      await downloadExport("/lead-assigning-logic/preview.csv", {}, `assigning-logic-preview-${activeSlug}.csv`, {
        method: "post", data: { cells },
      });
    } catch {
      toast.error("Preview download failed");
    }
  };

  const runRecoverPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/lead-assigning-logic/recover-no-history?dry_run=true");
      setMode("recover");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      if (mode === "recover") {
        const { data } = await api.post("/lead-assigning-logic/recover-no-history");
        setPreview(null);
        toast.success(`Recovered ownership for ${data.leads_affected} lead(s) from their last recorded agent`);
        loadDiag();
        load();
        return;
      }
      if (mode === "recalc") {
        const { data } = await api.post("/lead-assigning-logic/recalculate");
        setPreview(null);
        toast.success("Recalculating all leads against the current matrix…");
        setJob({ id: data.job_id, status: "queued", processed: 0, total: 0 });
        pollJob(data.job_id);
        return;
      }
      const { data } = await api.post("/lead-assigning-logic", { cells });
      setPreview(null);
      toast.success(`Matrix saved as v${data.version} — recalculating all leads…`);
      setJob({ id: data.job_id, status: "queued", processed: 0, total: 0 });
      pollJob(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const rollback = async (version) => {
    if (!window.confirm(`Revert to matrix v${version}? All leads will be recalculated against it.`)) return;
    try {
      const { data } = await api.post("/lead-assigning-logic/rollback", { version });
      toast.success(`Reverted to v${version} (saved as v${data.version}) — recalculating…`);
      setJob({ id: data.job_id, status: "queued", processed: 0, total: 0 });
      pollJob(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };

  const downloadJobReport = async (jobId, kind) => {
    try {
      await downloadExport(`/lead-assigning-logic/jobs/${jobId}/report.csv`, { kind },
        `recalculation-${kind}-${jobId.slice(0, 8)}.csv`);
    } catch {
      toast.error("Report not available");
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 p-6" data-testid="assigning-logic-loading">Loading assigning logic…</div>;
  }
  if (!cfg) return null;

  const groupsMeta = cfg.groups || [];
  const groups = groupsMeta.map((g) => g.key);
  const canManage = cfg.can_manage;

  return (
    <div className="space-y-4" data-testid="assigning-logic">
      {/* Ownership health — explains any lead that has a disposition but no owner */}
      {diag && (
        <Card className={diag.replayable > 0 ? "border-amber-300 bg-amber-50/50" : ""}
              data-testid="ownership-diagnostics">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1">
                <div className="text-xs font-semibold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className={`w-3.5 h-3.5 ${diag.replayable ? "text-amber-500" : "text-slate-400"}`} />
                  Ownership health
                </div>
                <div className="text-[11px] text-slate-600">
                  <strong data-testid="diag-unassigned-total">{diag.unassigned_with_disposition}</strong> lead(s)
                  carry a disposition but have no owner —{" "}
                  <strong data-testid="diag-replayable">{diag.replayable}</strong> would be assigned by
                  running the matrix now,{" "}
                  <strong data-testid="diag-no-history">{diag.no_history}</strong> have no logged
                  disposition history (skipped by design),{" "}
                  <strong data-testid="diag-owner-inactive">{diag.owner_inactive ?? 0}</strong> point to a
                  deactivated agent (left unassigned),{" "}
                  <strong>{diag.matrix_unassigned}</strong> stay unowned because the matrix says Unchanged.
                </div>
                <div className="text-[10px] text-slate-400">
                  {diag.last_recalculation
                    ? `Last recalculation ${(diag.last_recalculation.finished_at || "").slice(0, 16).replace("T", " ")} · matrix v${diag.last_recalculation.matrix_version} · ${diag.last_recalculation.summary?.leads_changed ?? 0} reassigned`
                    : "This client has never been recalculated — saving the matrix or running it now derives ownership from each lead's disposition history."}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-7 text-[10px]"
                        onClick={() => downloadExport("/lead-assigning-logic/diagnostics",
                          { format: "csv" }, `unassigned-with-disposition-${activeSlug}.csv`)}
                        data-testid="diag-csv-btn">
                  <FileDown className="w-3 h-3 mr-1" /> Download list
                </Button>
                {canManage && (diag.no_history ?? 0) > 0 && (
                  <Button variant="outline" size="sm" className="h-7 text-[10px] border-amber-400 text-amber-800"
                          disabled={previewing} onClick={runRecoverPreview}
                          data-testid="recover-no-history-btn">
                    {previewing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <UserCheck className="w-3 h-3 mr-1" />}
                    Recover {diag.no_history} from last agent
                  </Button>
                )}
                {canManage && (
                  <Button size="sm" className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700"
                          disabled={previewing} onClick={runRecalcPreview}
                          data-testid="run-recalculation-btn">
                    {previewing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <RefreshCw className="w-3 h-3 mr-1" />}
                    Run recalculation now
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-indigo-600" /> Assigning Logic
            </CardTitle>
            <p className="text-[11px] text-slate-500 mt-1">
              For each combination of a lead&apos;s current state and the group of the disposition
              being saved: <strong>New</strong> assigns the lead to the agent who saved it,
              <strong> Unchanged</strong> keeps the current assignee.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]" data-testid="matrix-version">
              v{cfg.version}
            </Badge>
            {canManage && (
              <>
                <Button variant="outline" size="sm" disabled={!dirty}
                        onClick={() => setCells(JSON.parse(JSON.stringify(cfg.cells)))}
                        data-testid="matrix-reset-btn">
                  Reset
                </Button>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                        disabled={!dirty || previewing} onClick={runPreview}
                        data-testid="matrix-save-btn">
                  {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Save changes
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="w-full text-xs" data-testid="matrix-grid">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400">
                    Lead state ↓ / Saved ▸
                  </th>
                  {groups.map((g) => (
                    <th key={g} className="text-left px-3 py-2" data-testid={`matrix-col-${g}`}>
                      <div className="font-semibold text-slate-700">{groupName(g)}</div>
                      <div className="text-[10px] text-slate-400 font-normal">
                        {groupsMeta.find((m) => m.key === g)?.meaning || ""}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(cfg.rows || []).map((r) => (
                  <tr key={r} className="border-t border-slate-100" data-testid={`matrix-row-${r}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-700">{rowLabel(r)}</div>
                      <div className="text-[10px] text-slate-400">
                        {cfg.lead_counts?.[r] ?? 0} lead(s)
                      </div>
                    </td>
                    {groups.map((c) => (
                      <td key={c} className="px-3 py-1.5">
                        <Select
                          value={(cells[r] || {})[c] || "unchanged"}
                          disabled={!canManage}
                          onValueChange={(v) => setCell(r, c, v)}
                        >
                          <SelectTrigger className="h-7 text-xs w-[130px]"
                                         data-testid={`matrix-cell-${r}-${c}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACTIONS.map((a) => (
                              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400">
            Last changed {(cfg.updated_at || "").slice(0, 16).replace("T", " ")} by {cfg.updated_by || "—"}.
            Manual reassignments and Arabic language routing always override the matrix and survive recalculation.
          </p>
        </CardContent>
      </Card>

      {/* Running / last recalculation job */}
      {job && (
        <Card className="border-indigo-200 bg-indigo-50/40" data-testid="recalc-job-card">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-indigo-900 flex items-center gap-2">
                {job.status === "running" || job.status === "queued"
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <History className="w-3.5 h-3.5" />}
                Recalculation — {job.status}
              </div>
              {job.status === "done" && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-7 text-[10px]"
                          onClick={() => downloadJobReport(job.id, "changes")}
                          data-testid="recalc-report-btn">
                    <FileDown className="w-3 h-3 mr-1" /> Change report
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-[10px]"
                          onClick={() => downloadJobReport(job.id, "exceptions")}
                          data-testid="recalc-exceptions-btn">
                    <FileDown className="w-3 h-3 mr-1" /> Exceptions
                  </Button>
                </div>
              )}
            </div>
            <Progress value={job.total ? Math.round(((job.processed || 0) / job.total) * 100) : 0}
                      className="h-1.5" />
            <div className="flex flex-wrap gap-2 text-[10px]" data-testid="recalc-job-summary">
              <Badge variant="outline">Scanned: {job.processed ?? 0}/{job.total ?? 0}</Badge>
              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                Reassigned: {job.summary?.leads_changed ?? job.changed ?? 0}
              </Badge>
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                Skipped (incomplete history): {job.summary?.exceptions ?? job.skipped ?? 0}
              </Badge>
              {(job.summary?.deactivated ?? 0) > 0 && (
                <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                  Deactivated owners cleared: {job.summary.deactivated}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Version history */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" /> Version history
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-1.5">Version</th>
                  <th className="text-left px-2 py-1.5">Saved</th>
                  <th className="text-left px-2 py-1.5">By</th>
                  <th className="text-left px-2 py-1.5">Changed cells</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {(cfg.versions || []).map((v) => (
                  <tr key={v.version} className="border-t border-slate-100"
                      data-testid={`matrix-version-${v.version}`}>
                    <td className="px-3 py-1.5 font-mono">
                      v{v.version}
                      {v.version === cfg.version && (
                        <Badge variant="outline" className="ml-1.5 text-[9px] text-emerald-700 border-emerald-300">
                          current
                        </Badge>
                      )}
                      {v.rollback_of && (
                        <Badge variant="outline" className="ml-1.5 text-[9px] text-slate-400">
                          rollback of v{v.rollback_of}
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500 font-mono text-[10px]">
                      {(v.created_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">{v.created_by_name || v.created_by}</td>
                    <td className="px-2 py-1.5 text-slate-500">
                      {(v.diff || []).length === 0 ? "—" : (v.diff || []).map((d) => (
                        <span key={`${d.row}-${d.column}`} className="mr-2 whitespace-nowrap">
                          {rowLabel(d.row)}→{d.column}: {d.from} ⇒ {d.to}
                        </span>
                      ))}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {canManage && v.version !== cfg.version && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                                onClick={() => rollback(v.version)}
                                data-testid={`matrix-rollback-${v.version}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Revert
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Change preview + explicit confirmation */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-lg" data-testid="matrix-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {mode === "recover" ? "Recover ownership from last agent"
                : mode === "recalc" ? "Recalculate ownership" : "Confirm assignment change"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {mode === "recover"
                ? "These leads carry a disposition that was never logged as a disposition event (for example set by the Wrong Number action). Each is assigned to the agent on its most recent recorded action, and only where the matrix says New for Unassigned."
                : mode === "recalc"
                  ? "Every lead is replayed against the current matrix using its logged disposition history. Manual reassignments and language routing are preserved."
                  : "Saving recalculates every lead against the new matrix. Manual reassignments and language routing are preserved."}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="border rounded p-2" data-testid="preview-leads-affected">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Leads reassigned</div>
                  <div className="text-lg font-semibold text-slate-900">{preview.leads_affected}</div>
                </div>
                <div className="border rounded p-2" data-testid="preview-agents-affected">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Agents affected</div>
                  <div className="text-lg font-semibold text-slate-900">{preview.agents_affected}</div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500">
                {mode === "recover"
                  ? `${preview.scanned} lead(s) examined · ${preview.unresolved ?? 0} could not be recovered (no agent recorded, inactive agent, or the matrix says Unchanged)`
                  : `${preview.scanned} lead(s) scanned · ${preview.exceptions} skipped (incomplete disposition history)`}
                {mode !== "recover" && preview.deactivated > 0 && ` · ${preview.deactivated} owner(s) deactivated`}
              </div>
              <div className="text-[11px] text-slate-600 max-h-24 overflow-y-auto border rounded p-2"
                   data-testid="preview-diff">
                {mode !== "save"
                  ? (preview.sample || []).slice(0, 12).map((s) => (
                      <div key={s.lead_id}>
                        {s.customer_id} · {s.customer_name} → <strong>{s.new_assignee}</strong>
                        <span className="text-slate-400"> ({s.reason})</span>
                      </div>
                    ))
                  : (preview.diff || []).length === 0
                    ? "No cell changes."
                    : preview.diff.map((d) => (
                        <div key={`${d.row}-${d.column}`}>
                          {rowLabel(d.row)} → saved {d.column}: <strong>{d.from}</strong> ⇒ <strong>{d.to}</strong>
                        </div>
                      ))}
              </div>
              <Button variant="outline" size="sm" className="h-7 text-[10px]"
                      onClick={downloadPreview} data-testid="preview-download-btn">
                <FileDown className="w-3 h-3 mr-1" /> Download preview list (CSV)
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="preview-cancel-btn">
              Cancel
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={saving}
                    onClick={confirmSave} data-testid="preview-confirm-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              {mode === "recover" ? "Recover" : mode === "recalc" ? "Assign" : "Save & recalculate"} {preview?.leads_affected ?? 0} lead(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
